import { useMemo } from 'react';
import { AuditAction } from '@etiquetachef/types';
import { useAuditEvents } from '@/features/audit/useAuditEvents';
import type { AuditEventRecord } from '@/lib/firestoreAudit';

/** Ações de audit que CONCEDEM acesso admin (as quatro callables que gravam claim). */
const GRANT_ACTIONS: ReadonlySet<string> = new Set([
  AuditAction.SUPER_ADMIN_BOOTSTRAPPED,
  AuditAction.SUPER_ADMIN_GRANTED,
  AuditAction.ADMIN_ROLE_GRANTED,
  AuditAction.ADMIN_ROLE_CHANGED,
]);

export type SuperAdminTrailState =
  | { kind: 'LOADING' }
  | { kind: 'READY'; latestGrantByUid: ReadonlyMap<string, AuditEventRecord> }
  | { kind: 'ERROR'; error: unknown };

/**
 * Última concessão de acesso admin por uid, das entradas RECENTES de `/audit`
 * — informação SECUNDÁRIA da tela ("concedido em X por Y").
 *
 * Origem: 1f372df7a — 2026-04-26 17:49 +0200 (Sprint 2, página
 * /settings/super-admins; sem issue no commit).
 *
 * Por quê: até a #795 este hook ERA a fonte da lista de admins, reconstruída
 * das últimas 100 entradas de `/audit` de todas as ações — admin concedido há
 * mais tempo sumia (e não podia ser revogado), e `ADMIN_ROLE_*` era ignorado.
 * A lista agora vem do Firebase Auth (`useAdminUsers`); aqui sobra só o
 * rótulo de quem concedeu. Ausência de evento NÃO significa "não é admin" —
 * só que a concessão é mais antiga que a janela de `AUDIT_LIST_LIMIT`.
 *
 * Contrato: lista global de `/audit` por `at desc` (limite `AUDIT_LIST_LIMIT`),
 * filtrada no cliente por `GRANT_ACTIONS`; o mapa guarda o evento mais recente
 * de cada `targetId`.
 *
 * Usado por: `routes/SuperAdminsPage.tsx`.
 *
 * Falhas conhecidas:
 *  - Até 2026-10-03 (#795, ADM-6): fonte da lista de admins ativos; perdia
 *    admins e não lia `ADMIN_ROLE_GRANTED`/`ADMIN_ROLE_CHANGED`.
 *
 * Histórico:
 *  - 2026-10-03 14:13 +0200 — (#795, ADM-6) deixa de montar a lista; vira só
 *    o rótulo de concessão, incluindo `ADMIN_ROLE_*`.
 */
export function useSuperAdminAuditTrail(): SuperAdminTrailState {
  // Filtrar por 4 actions exigiria `where('action', 'in', [...])` + `orderBy('at')`
  // e índice novo; a lista geral filtrada no cliente atende o rótulo.
  const state = useAuditEvents(useMemo(() => ({}), []));

  return useMemo((): SuperAdminTrailState => {
    if (state.kind === 'LOADING') return { kind: 'LOADING' };
    if (state.kind === 'ERROR') return { kind: 'ERROR', error: state.error };
    // Ordem do audit log já é desc por `at`: o primeiro de cada uid é o mais recente.
    const latest = new Map<string, AuditEventRecord>();
    for (const event of state.result.events) {
      if (!GRANT_ACTIONS.has(event.action)) continue;
      if (!latest.has(event.targetId)) latest.set(event.targetId, event);
    }
    return { kind: 'READY', latestGrantByUid: latest };
  }, [state]);
}
